/**
 * Unified Dashboard - Best of both worlds
 * Smart insights + Zero-click actions + Panel integration
 */

import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { trpc } from '../lib/trpc'
import { useAuthStore } from '../stores/auth-store'
import { useUIStore } from '../stores/ui-store'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import { Input } from '../components/ui/input'
import { 
  Clock, AlertCircle, CheckCircle2, ArrowRight, 
  Zap, Target, TrendingUp, Users, Calendar,
  Plus, Search, Filter, MoreHorizontal,
  Brain, Flame, Timer, Award, Activity,
  KanbanSquare, FileText, MessageSquare
} from 'lucide-react'
import { cn } from '../lib/utils'
import { formatDistanceToNow } from 'date-fns'
import Layout from '../components/project/Layout'

export default function Dashboard() {
  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { openTaskCreationModal } = useUIStore()
  const [quickAction, setQuickAction] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  
  // Core data
  const { data: workspace } = trpc.workspace.get.useQuery(
    { id: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: recentTasks = [] } = trpc.dashboard.recentTasks.useQuery(
    { workspaceId: workspaceId!, limit: 8 },
    { enabled: !!workspaceId }
  )
  
  const { data: todayTasks = [] } = trpc.dashboard.todayTasks.useQuery(
    { workspaceId: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: stats } = trpc.dashboard.stats.useQuery(
    { workspaceId: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: upcomingDeadlines = [] } = trpc.dashboard.upcomingDeadlines.useQuery(
    { workspaceId: workspaceId!, days: 7 },
    { enabled: !!workspaceId }
  )
  
  const { data: recentActivity = [] } = trpc.activity.list.useQuery(
    { workspaceId: workspaceId!, limit: 10 },
    { enabled: !!workspaceId }
  )

  // Quick task creation
  const createTaskMutation = trpc.dashboard.quickCreateTask.useMutation({
    onSuccess: () => {
      setQuickAction('')
      // Refetch relevant data
      trpc.dashboard.recentTasks.invalidate()
      trpc.dashboard.todayTasks.invalidate()
      trpc.dashboard.stats.invalidate()
    }
  })

  // Smart analytics
  const analytics = useMemo(() => {
    if (!stats) return null
    
    const myTasks = recentTasks.filter(t => t.assigneeId === user?.id && t.status !== 'DONE')
    const myOverdue = upcomingDeadlines.filter(t => 
      t.assigneeId === user?.id && 
      t.dueDate && 
      new Date(t.dueDate) < new Date()
    )
    
    // Calculate critical/urgent items
    const criticalTasks = recentTasks.filter(t => t.priority === 'critical').length
    const highPriorityTasks = recentTasks.filter(t => t.priority === 'high').length
    const dueSoon = upcomingDeadlines.filter(t => 
      new Date(t.dueDate!) < new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
    ).length
    
    return {
      ...stats,
      myTasks: myTasks.length,
      myOverdue: myOverdue.length,
      criticalTasks,
      highPriorityTasks,
      dueSoon,
      focusScore: Math.max(0, 100 - (myOverdue.length * 20) - (criticalTasks * 15) - (dueSoon * 10))
    }
  }, [stats, recentTasks, upcomingDeadlines, user?.id])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      switch(e.key) {
        case 't':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            quickCreateTask()
          }
          break
        case 's':
        case '/':
          e.preventDefault()
          document.getElementById('search-input')?.focus()
          break
        case 'w':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            navigate(`/workspace/${workspaceId}`)
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [workspaceId, navigate])

  const quickCreateTask = () => {
    const title = prompt('Task title (or "command syntax"):')
    if (!title) return
    
    const parsed = parseTaskCommand(title)
    createTaskMutation.mutate({
      workspaceId: workspaceId!,
      title: parsed.title,
      priority: parsed.priority,
      dueDate: parsed.dueDate
    })
  }

  const parseTaskCommand = (input: string) => {
    const result = {
      title: input,
      priority: 'medium' as const,
      dueDate: undefined as Date | undefined
    }
    
    if (input.includes('#high') || input.includes('!')) {
      result.priority = 'high'
      result.title = result.title.replace(/#high|!/g, '').trim()
    }
    
    const dueMatch = input.match(/due:(\w+)/)
    if (dueMatch) {
      result.dueDate = parseDateShorthand(dueMatch[1])
      result.title = result.title.replace(/due:\w+/g, '').trim()
    }
    
    return result
  }

  const parseDateShorthand = (shorthand: string): Date | undefined => {
    const today = new Date()
    switch(shorthand.toLowerCase()) {
      case 'today':
        return today
      case 'tomorrow':
        return new Date(today.getTime() + 24 * 60 * 60 * 1000)
      default:
        return undefined
    }
  }

  const handleQuickAction = (action: string) => {
    const cmd = action.toLowerCase().trim()
    
    if (cmd.startsWith('new ')) {
      const title = cmd.replace('new ', '')
      createTaskMutation.mutate({
        workspaceId: workspaceId!,
        title,
        priority: 'medium'
      })
    } else if (cmd === 'workspace') {
      navigate(`/workspace/${workspaceId}`)
    }
    
    setQuickAction('')
  }

  if (!workspace || !analytics) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-krushr-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header with Quick Actions */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {workspace.name} Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                {analytics.focusScore >= 80 ? 'You\'re in the zone' : 
                 analytics.focusScore >= 60 ? 'Making good progress' : 
                 'Let\'s optimize your workflow'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Input
                id="search-input"
                placeholder="Quick action: 'new task', 'workspace' (press 's')"
                value={quickAction}
                onChange={(e) => setQuickAction(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuickAction(quickAction)}
                className="w-80"
              />
              <Button onClick={() => navigate(`/workspace/${workspaceId}`)}>
                Open Workspace
              </Button>
            </div>
          </div>

          {/* Quick Access Bar */}
          <div className="flex items-center gap-2 p-4 bg-white rounded-lg border">
            <Button
              size="sm"
              onClick={quickCreateTask}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Task (t)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/workspace/${workspaceId}`)}
              className="flex items-center gap-2"
            >
              <KanbanSquare className="w-4 h-4" />
              Workspace (w)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/notes`)}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Notes
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/chat`)}
              className="flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Chat
            </Button>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-6 gap-4">
            <MetricCard
              title="Critical Tasks"
              value={analytics.criticalTasks}
              icon={AlertCircle}
              color="red"
              onClick={() => navigate(`/workspace/${workspaceId}?filter=critical`)}
            />
            <MetricCard
              title="Due Soon"
              value={analytics.dueSoon}
              icon={Timer}
              color="orange"
              onClick={() => navigate(`/workspace/${workspaceId}?filter=due-soon`)}
            />
            <MetricCard
              title="My Overdue"
              value={analytics.myOverdue}
              icon={Clock}
              color="blue"
              onClick={() => navigate(`/workspace/${workspaceId}?filter=overdue-mine`)}
            />
            <MetricCard
              title="Completed"
              value={analytics.completedTasks}
              icon={CheckCircle2}
              color="green"
            />
            <MetricCard
              title="Focus Score"
              value={analytics.focusScore}
              icon={Brain}
              color="purple"
              showProgress={true}
            />
            <MetricCard
              title="Team Members"
              value={analytics.teamMembers}
              icon={Users}
              color="gray"
            />
          </div>

          {/* High Priority & Time-Sensitive Tasks - Top Priority */}
          {(recentTasks.filter(t => t.priority === 'high' || t.priority === 'critical').length > 0 || 
            upcomingDeadlines.filter(t => new Date(t.dueDate!) < new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)).length > 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h2 className="font-semibold text-red-900">Urgent Attention Required</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Critical/High Priority Tasks */}
                {recentTasks.filter(t => t.priority === 'high' || t.priority === 'critical').slice(0, 3).map((task) => (
                  <div
                    key={task.id}
                    className="bg-white border border-red-200 rounded-lg p-3 hover:shadow-md cursor-pointer transition-shadow"
                    onClick={() => navigate(`/workspace/${workspaceId}?task=${task.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-sm text-gray-900">{task.title}</h3>
                        <p className="text-xs text-gray-600 mt-1">
                          {task.assignee?.name || 'Unassigned'} • {task.project?.name || 'No project'}
                        </p>
                      </div>
                      <Badge 
                        variant={task.priority === 'critical' ? 'destructive' : 'secondary'}
                        className={cn(
                          "text-xs",
                          task.priority === 'critical' && "bg-red-600 text-white",
                          task.priority === 'high' && "bg-orange-100 text-orange-800"
                        )}
                      >
                        {task.priority}
                      </Badge>
                    </div>
                    {task.dueDate && (
                      <div className="mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-red-500" />
                        <span className="text-xs text-red-600 font-medium">
                          Due {formatDistanceToNow(new Date(task.dueDate), { addSuffix: true })}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Tasks Due in Next 48 Hours */}
                {upcomingDeadlines
                  .filter(t => new Date(t.dueDate!) < new Date(Date.now() + 2 * 24 * 60 * 60 * 1000))
                  .slice(0, 3)
                  .map((task) => (
                    <div
                      key={`deadline-${task.id}`}
                      className="bg-white border border-orange-200 rounded-lg p-3 hover:shadow-md cursor-pointer transition-shadow"
                      onClick={() => navigate(`/workspace/${workspaceId}?task=${task.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-sm text-gray-900">{task.title}</h3>
                          <p className="text-xs text-gray-600 mt-1">
                            {task.assignee?.name || 'Unassigned'} • {task.project?.name || 'No project'}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">
                          {task.priority}
                        </Badge>
                      </div>
                      <div className="mt-2 flex items-center gap-1">
                        <Timer className="w-3 h-3 text-orange-500" />
                        <span className="text-xs text-orange-600 font-medium">
                          Due {formatDistanceToNow(new Date(task.dueDate!), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Priority Tasks */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    All Priority Tasks
                  </div>
                  <Badge variant="secondary">{recentTasks.filter(t => t.priority === 'high' || t.priority === 'critical').length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentTasks.filter(t => t.priority === 'high' || t.priority === 'critical').slice(0, 5).map((task) => (
                  <TaskQuickView
                    key={task.id}
                    task={task}
                    onClick={() => navigate(`/workspace/${workspaceId}?task=${task.id}`)}
                  />
                ))}
                {recentTasks.filter(t => t.priority === 'high' || t.priority === 'critical').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No urgent tasks 🎉</p>
                )}
              </CardContent>
            </Card>

            {/* Today's Focus */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    Today's Focus
                  </div>
                  <Badge variant="secondary">{todayTasks.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {todayTasks.slice(0, 5).map((task) => (
                  <TaskQuickView
                    key={task.id}
                    task={task}
                    onClick={() => navigate(`/workspace/${workspaceId}?task=${task.id}`)}
                  />
                ))}
                {todayTasks.length === 0 && (
                  <p className="text-gray-500 text-center py-4">Nothing scheduled today</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentActivity.slice(0, 5).map((activity, idx) => (
                  <div key={idx} className="text-sm text-gray-600">
                    <span className="font-medium">{activity.action}</span>
                    <span className="text-gray-400 ml-2">
                      {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                ))}
                {recentActivity.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No recent activity</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Deadlines */}
          {upcomingDeadlines.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Upcoming Deadlines (Next 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {upcomingDeadlines.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => navigate(`/workspace/${workspaceId}?task=${task.id}`)}
                    >
                      <div>
                        <p className="font-medium text-sm">{task.title}</p>
                        <p className="text-xs text-gray-500">
                          {task.assignee?.name || 'Unassigned'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : ''}
                        </p>
                        <Badge 
                          variant={task.priority === 'high' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {task.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  )
}

// Helper Components

function MetricCard({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  onClick, 
  showProgress = false 
}: {
  title: string
  value: number
  icon: any
  color: string
  onClick?: () => void
  showProgress?: boolean
}) {
  const colorClasses = {
    red: 'border-l-red-500 text-red-600',
    orange: 'border-l-orange-500 text-orange-600',
    blue: 'border-l-blue-500 text-blue-600',
    green: 'border-l-green-500 text-green-600',
    purple: 'border-l-purple-500 text-purple-600',
    gray: 'border-l-gray-500 text-gray-600'
  }

  const iconColors = {
    red: 'text-red-500',
    orange: 'text-orange-500',
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    gray: 'text-gray-500'
  }

  return (
    <Card 
      className={cn("border-l-4 cursor-pointer hover:shadow-md transition-shadow", 
        colorClasses[color as keyof typeof colorClasses])}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className={cn("text-2xl font-bold", colorClasses[color as keyof typeof colorClasses])}>
              {value}
            </p>
          </div>
          <Icon className={cn("w-8 h-8", iconColors[color as keyof typeof iconColors])} />
        </div>
        {showProgress && (
          <Progress value={value} className="mt-2" />
        )}
      </CardContent>
    </Card>
  )
}

function TaskQuickView({ task, onClick }: { task: any, onClick: () => void }) {
  return (
    <div 
      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
      onClick={onClick}
    >
      <div className="flex-1">
        <p className="text-sm font-medium">{task.title}</p>
        <p className="text-xs text-gray-500">
          {task.assignee?.name || 'Unassigned'} • {task.project?.name || 'No project'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge 
          variant={task.priority === 'high' ? 'destructive' : 'secondary'}
          className="text-xs"
        >
          {task.priority}
        </Badge>
        {task.dueDate && (
          <span className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(task.dueDate), { addSuffix: true })}
          </span>
        )}
      </div>
    </div>
  )
}